import React from 'react';

import { get, isEmpty } from 'lodash';
import MemberColumn from '../columnDefs/Member';
import Generic from '../columnDefs/Generic';
import { Row } from '@tanstack/react-table';
import { PostInsights } from '@frontend/gql/social/graphql';
import {
  SocialMediaThumbnail,
  getContentTypeLabel,
  isContentTypeEnum,
} from '@frontend/applications/Social/components/thumbnail/SocialMediaThumbnail';
import { SocialColumn } from '../../columnSelectorDialog/hooks/useColumnDialog';
import Percentage from '../columnDefs/Percentage';
import Currency from '../columnDefs/Currency';
import { getSocialAccountLink } from '@frontend/utils';
import { isNetworkIdentifier } from '@components';
import { formatDisplayDate } from '../../date-range-form/utils';
import { ENTITY_TYPE_TO_POST_TYPE, POST_TYPE_LABELS } from '@frontend/applications/Social/types';
import { formatDurationFromSeconds } from '../../../utils/format';
import { UI_LABELS } from '../../filters-form/mention-source-select';

export const POST_TABLE_COLUMN_CONFIG_VERSION = 15; // must update this value if you change anything below

export interface PostTableColumn<T = any> extends SocialColumn {
  header: string;
  id: string;
  enableSorting?: boolean;
  enableGlobalFilter?: boolean;
  hidden: boolean;
  size?: number;
  minSize?: number;
  maxSize?: number;
  accessorFn?: (row: T) => unknown;
  accessorKey?: string; // accessorKey or accessorFn should be set
  cell: (props: { row: Row<T> }) => React.ReactNode;
  csvValue: (props: { data: T }) => string | null | undefined;
  meta?: {
    headerGroupBorder: {
      borderColor: string;
    };
  };
}

const baseDefaultVisiblePostColumnIds = [
  'content',
  'member',
  'socialHandle',
  'member.segment',
  'postPermalink',
  'entityType',
  'postDate',
  'postUpdateDate',
  'project',
  'total.engagements',
  'total.engagementRate',
  'total.impressions',
  'total.potentialReach',
  'total.tmv',
];

// Helper function to add/remove paid and organic metrics from any column array
export const adjustColumnsForPaidMetrics = (columns: string[], includePaidMetrics: boolean): string[] => {
  const paidMetricsColumns = ['total.paidEngagements', 'total.paidImpressions'];
  const organicMetricsColumns = ['total.organicEngagements', 'total.organicImpressions'];

  if (includePaidMetrics) {
    // Add organic and paid metrics if not already present
    const result = [...columns];

    // Add organic engagements after engagements if not present
    if (!result.includes('total.organicEngagements')) {
      const engagementsIndex = result.indexOf('total.engagements');
      if (engagementsIndex !== -1) {
        result.splice(engagementsIndex + 1, 0, 'total.organicEngagements');
      }
    }

    // Add paid engagements after organic engagements (or after engagements if organic not present)
    if (!result.includes('total.paidEngagements')) {
      const organicEngagementsIndex = result.indexOf('total.organicEngagements');
      const engagementsIndex = result.indexOf('total.engagements');
      const insertIndex = organicEngagementsIndex !== -1 ? organicEngagementsIndex + 1 : engagementsIndex + 1;
      if (insertIndex > 0) {
        result.splice(insertIndex, 0, 'total.paidEngagements');
      }
    }

    // Add organic impressions after impressions if not present
    if (!result.includes('total.organicImpressions')) {
      const impressionsIndex = result.indexOf('total.impressions');
      if (impressionsIndex !== -1) {
        result.splice(impressionsIndex + 1, 0, 'total.organicImpressions');
      }
    }

    // Add paid impressions after organic impressions (or after impressions if organic not present)
    if (!result.includes('total.paidImpressions')) {
      const organicImpressionsIndex = result.indexOf('total.organicImpressions');
      const impressionsIndex = result.indexOf('total.impressions');
      const insertIndex = organicImpressionsIndex !== -1 ? organicImpressionsIndex + 1 : impressionsIndex + 1;
      if (insertIndex > 0) {
        result.splice(insertIndex, 0, 'total.paidImpressions');
      }
    }

    return result;
  } else {
    // Remove paid and organic metrics columns if present
    return columns.filter((col) => !paidMetricsColumns.includes(col) && !organicMetricsColumns.includes(col));
  }
};

export const getDefaultVisiblePostColumnIds = (includePaidMetrics: boolean = false): string[] => {
  return adjustColumnsForPaidMetrics(baseDefaultVisiblePostColumnIds, includePaidMetrics);
};

export const defaultVisiblePostColumnIds = getDefaultVisiblePostColumnIds(false); // Default to no paid metrics

export const getColumns = (ids?: string[]): PostTableColumn<PostInsights>[] => {
  return !isEmpty(ids)
    ? AllColumns.filter((col) => ids!.includes(col.id))
    : AllColumns.filter((col) => defaultVisiblePostColumnIds.includes(col.id));
};

export const getColumnsWithHiddenColumns = (
  ids: string[] = defaultVisiblePostColumnIds,
  handlers?: {
    onContentClick?: (post: PostInsights) => void;
  },
): PostTableColumn<PostInsights>[] => {
  const applyHidden = (col: PostTableColumn<PostInsights>): PostTableColumn<PostInsights> => ({
    ...col,
    hidden: !ids.includes(col.id),
    subColumns: col.subColumns?.map((subCol) => applyHidden(subCol as PostTableColumn<PostInsights>)),
  });
  return createAllColumns(handlers).map(applyHidden);
};

export const createAllColumns = (handlers?: {
  onContentClick?: (post: PostInsights) => void;
}): PostTableColumn<PostInsights>[] => [
  {
    header: 'Content',
    id: 'content',
    accessorKey: 'content',
    enableSorting: true,
    hidden: false,
    isFixed: true,
    maxSize: 90,
    csvValue: ({ data }) => {
      return get(data, 'postImageUrl');
    },
    cell: ({ row }) => {
      const postUrl = get(row.original, 'postPermalink') ?? '';
      const postImageUrl = get(row.original, 'postImageUrl') ?? '';
      const memberName = get(row.original, 'member.name') ?? '';
      const entityType = get(row.original, 'entityType') ?? '';
      const entityNetwork = get(row.original, 'entityNetwork') ?? '';

      const thumbnail = (
        <SocialMediaThumbnail
          postUrl={postUrl}
          postImageUrl={postImageUrl || undefined}
          memberName={memberName}
          entityType={entityType}
          entityNetwork={entityNetwork}
        />
      );

      if (handlers?.onContentClick) {
        return (
          <div
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handlers.onContentClick!(row.original);
            }}
            data-dd-action-name="social-analytics-table-content-click"
          >
            {thumbnail}
          </div>
        );
      }

      return (
        <a href={postUrl} target="_blank" rel="noopener noreferrer" data-dd-action-name="social-analytics-table-post-link">
          {thumbnail}
        </a>
      );
    },
  },
  {
    header: 'Member',
    id: 'member',
    enableSorting: true,
    enableGlobalFilter: true,
    hidden: false,
    isFixed: true,
    size: 200,
    minSize: 50,
    maxSize: 300,
    accessorFn: (row) => row.member?.name ?? '',
    csvValue: ({ data }) => {
      const member = get(data, 'member');
      return member?.name ?? '';
    },
    cell: ({ row }) => {
      const member = get(row.original, 'member');
      if (!member) {
        return <Generic value="Unknown" aria-label="Member: Unknown" />;
      }
      const profilePicture = member.profilePicture || undefined;
      return (
        <MemberColumn
          name={member?.name || ''}
          image={profilePicture}
          url={member?.id ? `/members/${member.id}` : '#'}
          aria-label={`Member: ${member?.name || 'Unknown'}`}
          data-dd-action-name="member-table-member-click"
        />
      );
    },
  },
  {
    header: 'Social Handle',
    id: 'socialHandle',
    enableSorting: true,
    enableGlobalFilter: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      const handle = get(data, 'accountUsername');
      return handle ?? '';
    },
    accessorFn: (row) => {
      const handle = get(row, 'accountUsername');
      return handle ?? '';
    },
    cell: ({ row }) => {
      const network = get(row.original, 'entityNetwork');
      const handle = get(row.original, 'accountUsername') ?? '';
      if (isNetworkIdentifier(network)) {
        const socialLink = getSocialAccountLink(network, handle);
        return (
          <a href={socialLink} target="_blank" rel="noopener noreferrer" data-dd-action-name="social-analytics-table-social-handle-link">
            <Generic value={handle} className="underline" aria-label={`Post Link: ${handle || 'Not available'}`} />
          </a>
        );
      }
      return <Generic value={handle ?? '-'} aria-label={`Post Link: ${handle || 'Not available'}`} />;
    },
  },
  {
    header: 'Post Link',
    id: 'postPermalink',
    accessorKey: 'postPermalink',
    enableGlobalFilter: true,
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return String(get(data, 'postPermalink') || '');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'postPermalink') || '';
      return (
        <a href={value} target="_blank" rel="noopener noreferrer" data-dd-action-name="social-analytics-table-post-permalink">
          <Generic value={value} className="underline" aria-label={`Post Link: ${value ?? 'Not available'}`} />
        </a>
      );
    },
  },
  {
    header: 'Network',
    id: 'entityNetwork',
    accessorKey: 'entityNetwork',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'entityNetwork');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'entityNetwork');
      return <Generic value={value} aria-label={`Network: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Post Type',
    id: 'entityType',
    accessorKey: 'entityType',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      const rawType = get(data, 'entityType');

      const postType = ENTITY_TYPE_TO_POST_TYPE[rawType];
      if (postType) {
        return POST_TYPE_LABELS[postType];
      }

      const enumType = isContentTypeEnum(rawType) ? rawType : null;
      return getContentTypeLabel(enumType);
    },
    cell: ({ row }) => {
      const rawType = get(row.original, 'entityType');

      const postType = ENTITY_TYPE_TO_POST_TYPE[rawType];
      if (postType) {
        return POST_TYPE_LABELS[postType];
      }

      const enumType = isContentTypeEnum(rawType) ? rawType : null;
      const value = getContentTypeLabel(enumType);
      return <Generic value={value} aria-label={`Post Type: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Caption',
    id: 'postCaption',
    accessorKey: 'postCaption',
    enableSorting: true,
    enableGlobalFilter: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'postCaption') || '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'postCaption') || '';
      return <Generic value={value} aria-label={`Caption: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Post Date',
    id: 'postDate',
    accessorKey: 'postDate',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      const value = get(data, 'postDate');
      return value ? formatDisplayDate(String(value)) : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'postDate') || '';
      const formattedDate = value ? formatDisplayDate(String(value)) : '';
      return <Generic value={formattedDate} aria-label={`Post Date: ${formattedDate || 'Not available'}`} />;
    },
  },
  {
    header: 'Last Update Date',
    id: 'postUpdateDate',
    accessorKey: 'postUpdateDate',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      const value = get(data, 'postUpdateDate');
      return value ? formatDisplayDate(String(value)) : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'postUpdateDate') || '';
      const formattedDate = value ? formatDisplayDate(String(value)) : '';
      return <Generic value={formattedDate} aria-label={`Last Update Date: ${formattedDate || 'Not available'}`} />;
    },
  },
  {
    header: 'Content Source',
    id: 'mentionSource',
    accessorKey: 'mentionSource',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      const value = get(data, 'mentionSource');
      return value ? UI_LABELS[value] || value : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'mentionSource');
      const label = value ? UI_LABELS[value] || value : '-';
      return <Generic value={label} aria-label={`Content Source: ${label || 'Not available'}`} />;
    },
  },
  {
    header: 'Project',
    id: 'project',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'project.name');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'project.name');
      return <Generic value={value} aria-label={`Project: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Group',
    id: 'group',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'group.name');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'group.name');
      return <Generic value={value} aria-label={`Group: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Engagement',
    id: 'total.engagements',
    accessorKey: 'total.engagements',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.engagements');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.engagements');
      return <Generic align={'right'} value={value} aria-label={`Engagement: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Organic Engagements',
    id: 'total.organicEngagements',
    accessorKey: 'total.organicEngagements',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.organicEngagements');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.organicEngagements');
      return <Generic align={'right'} value={value} aria-label={`Organic Engagements: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Paid Engagements',
    id: 'total.paidEngagements',
    accessorKey: 'total.paidEngagements',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.paidEngagements');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.paidEngagements');
      return <Generic align={'right'} value={value} aria-label={`Paid Engagements: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Engagement Rate',
    id: 'total.engagementRate',
    accessorKey: 'total.engagementRate',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.engagementRate');
    },
    cell: ({ row }) => {
      const percentValue = get(row.original, 'total.engagementRate');
      return <Percentage value={percentValue} aria-label={`Engagement Rate: ${percentValue || 'Not available'}`} />;
    },
  },
  {
    header: 'Likes',
    id: 'total.likes',
    accessorKey: 'total.likes',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.likes');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.likes');
      return <Generic align={'right'} value={value} aria-label={`Likes: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Comments',
    id: 'total.comments',
    accessorKey: 'total.comments',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.comments');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.comments');
      return <Generic align={'right'} value={value} aria-label={`Comments: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Saves',
    id: 'total.saves',
    accessorKey: 'total.saves',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.saves');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.saves');
      return <Generic align={'right'} value={value} aria-label={`Saves: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Shares',
    id: 'total.shares',
    accessorKey: 'total.shares',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.shares');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.shares');
      return <Generic align={'right'} value={value} aria-label={`Shares: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Impressions',
    id: 'total.impressions',
    accessorKey: 'total.impressions',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.impressions');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.impressions');
      return <Generic align={'right'} value={value} aria-label={`Impressions: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Organic Impressions',
    id: 'total.organicImpressions',
    accessorKey: 'total.organicImpressions',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.organicImpressions');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.organicImpressions');
      return <Generic align={'right'} value={value} aria-label={`Organic Impressions: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Paid Impressions',
    id: 'total.paidImpressions',
    accessorKey: 'total.paidImpressions',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.paidImpressions');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.paidImpressions');
      return <Generic align={'right'} value={value} aria-label={`Paid Impressions: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Total Reach',
    id: 'total.reach',
    accessorKey: 'total.reach',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.reach');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.reach');
      return <Generic align={'right'} value={value} aria-label={`Reach: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Potential Reach',
    id: 'total.potentialReach',
    accessorKey: 'total.potentialReach',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.potentialReach');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.potentialReach');
      return <Generic align={'right'} value={value} aria-label={`Potential Reach: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Total View Time',
    id: 'total.viewTimeTotalSeconds',
    accessorKey: 'total.viewTimeTotalSeconds',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      const value = get(data, 'total.viewTimeTotalSeconds');
      const formattedValue = formatDurationFromSeconds(value);
      return formattedValue || '-';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.viewTimeTotalSeconds');
      const formattedValue = formatDurationFromSeconds(value);
      return (
        <Generic
          align={'right'}
          value={formattedValue || '-'}
          aria-label={`Total View Time: ${formattedValue || 'Not available'}`}
        />
      );
    },
  },
  {
    header: 'Avg View Time',
    id: 'total.viewTimeAvgSeconds',
    accessorKey: 'total.viewTimeAvgSeconds',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      const value = get(data, 'total.viewTimeAvgSeconds');
      const formattedValue = formatDurationFromSeconds(value);
      return formattedValue || '-';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.viewTimeAvgSeconds');
      const formattedValue = formatDurationFromSeconds(value);
      return (
        <Generic
          align={'right'}
          value={formattedValue || '-'}
          aria-label={`Average View Time: ${formattedValue || 'Not available'}`}
        />
      );
    },
  },
  {
    header: 'TMV',
    id: 'total.tmv',
    accessorKey: 'total.tmv',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.tmv');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.tmv');
      return <Currency value={value} aria-label={`TMV: ${value ?? 0}`} />;
    },
  },
  {
    header: 'Follower Growth',
    id: 'total.followerGrowth',
    accessorKey: 'total.followerGrowth',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.followerGrowth');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.followerGrowth');
      return <Generic align={'right'} value={value} aria-label={`Follower Growth: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Impression Rate',
    id: 'total.impressionRate',
    accessorKey: 'total.impressionRate',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.impressionRate');
    },
    cell: ({ row }) => {
      const percentValue = get(row.original, 'total.impressionRate');
      return <Percentage value={percentValue} aria-label={`Impression Rate: ${percentValue || 'Not available'}`} />;
    },
  },
];

// Backward compatibility: create AllColumns as default instance
export const AllColumns = createAllColumns();
